import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  DatePicker,
  Select,
  message,
  Spin,
  Space,
  Divider,
  Typography
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import logger from '../../services/logger';

const { Option } = Select;
const { TextArea } = Input;

const EditInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/billing/invoices/${id}`);
      setInvoice(response.data);
      
      // Form'u doldur
      form.setFieldsValue({
        invoice_number: response.data.invoice_number,
        description: response.data.description,
        notes: response.data.notes,
        due_date: response.data.due_date ? dayjs(response.data.due_date) : null,
        issue_date: response.data.issue_date ? dayjs(response.data.issue_date) : null,
        payment_date: response.data.payment_date ? dayjs(response.data.payment_date) : null,
        status: response.data.status,
        subtotal: response.data.subtotal,
        vat_amount: response.data.vat_amount,
        total_amount: response.data.total_amount
      });
    } catch (error) {
      logger.error('Fatura yüklenemedi:', error);
      if (error.response?.status === 403) {
        message.error('Bu işlem için yetkiniz bulunmuyor.');
      } else if (error.response?.status === 401) {
        message.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        navigate('/login');
      } else {
        message.error('Fatura yüklenirken bir hata oluştu.');
      }
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleSave = async (values) => {
    try {
      setSaving(true);
      
      const updateData = {
        description: values.description,
        notes: values.notes,
        issue_date: values.issue_date ? values.issue_date.format('YYYY-MM-DD HH:mm:ss') : null,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD HH:mm:ss') : null,
        status: values.status
      };

      await axios.put(`/api/v1/billing/invoices/${id}`, updateData);
      
      message.success('Fatura başarıyla güncellendi.');
      navigate(`/billing/${id}`);
    } catch (error) {
      logger.error('Fatura güncellenemedi:', error);
      if (error.response?.status === 403) {
        message.error('Bu işlem için yetkiniz bulunmuyor.');
      } else if (error.response?.status === 401) {
        message.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        navigate('/login');
      } else {
        message.error('Fatura güncellenirken bir hata oluştu.');
      }
    } finally {
      setSaving(false);
    }
  };

  const getStatusOptions = () => {
    const statuses = [
      { value: 'pending', label: 'Bekliyor' },
      { value: 'paid', label: 'Ödendi' },
      { value: 'overdue', label: 'Vadesi Geçti' },
    ];

    return statuses.map(status => (
      <Option key={status.value} value={status.value}>
        {status.label}
      </Option>
    ));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Fatura yükleniyor...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Fatura bulunamadı.</p>
        <Button type="primary" onClick={() => navigate('/billing')}>
          Faturalara Dön
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(`/billing/${id}`)}
          style={{ marginBottom: 16 }}
        >
          Fatura Detayına Dön
        </Button>
        
        <Typography.Title level={2} style={{ margin: 0 }}>
          <EditOutlined /> Fatura Düzenle - #{invoice.invoice_number}
        </Typography.Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          status: 'pending'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Fatura Bilgileri">
              <Form.Item
                label="Fatura Numarası"
                name="invoice_number"
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                label="Durum"
                name="status"
                rules={[{ required: true, message: 'Durum seçiniz' }]}
              >
                <Select placeholder="Durum seçiniz">
                  {getStatusOptions()}
                </Select>
              </Form.Item>

              <Form.Item
                label="Fatura Tarihi"
                name="issue_date"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>

              <Form.Item
                label="Ödeme Tarihi"
                name="payment_date"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Fatura Detayları">
              <Form.Item
                label="Açıklama"
                name="description"
              >
                <TextArea rows={4} placeholder="Fatura açıklaması" />
              </Form.Item>

              <Form.Item
                label="Notlar"
                name="notes"
              >
                <TextArea rows={4} placeholder="Ek notlar" />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <Card title="Ara Toplam">
              <Form.Item name="subtotal">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₺\s?|(,*)/g, '')}
                  disabled
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={8}>
            <Card title="KDV">
              <Form.Item name="vat_amount">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₺\s?|(,*)/g, '')}
                  disabled
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={8}>
            <Card title="Genel Toplam">
              <Form.Item name="total_amount">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₺\s?|(,*)/g, '')}
                  disabled
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row justify="center">
          <Space>
            <Button 
              onClick={() => navigate(`/billing/${id}`)}
              size="large"
            >
              İptal
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              Kaydet
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default EditInvoice;
